/* eslint-disable kf/svg-file-name */
import { t } from "@lingui/macro";
import { i18nMark } from "@lingui/react";

import { i18n } from "base/i18n";
import { useAccountStore } from "base/stores";
import { FORMAT_TYPES } from "widgets/inputs";

import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
  StrikeButton,
  LinkButton,
  Heading,
  TextColor,
  CodeButton,
  BlockQuoteButton,
  ULList,
  OLList
} from "@/components/forum/toolbar/";

import Angry from "../icons/reaction/Angry.svg";
import Birthday from "../icons/reaction/Birthday.svg";
import Bouquet from "../icons/reaction/Bouquet.svg";
import Haha from "../icons/reaction/Haha.svg";
import LikeMain from "../icons/reaction/Like-main.svg";
import LikeOutline from "../icons/reaction/Like-outline.svg";
import LikeOutlineBlack from "../icons/reaction/LikeOutlineBlack.svg";
import Like from "../icons/reaction/Like.svg";
import Love from "../icons/reaction/Love.svg";
import FoldedHands from "../icons/reaction/Foldedhands.svg";
import Sad from "../icons/reaction/Sad.svg";
import Wow from "../icons/reaction/Wow.svg";

export const CHILD_TYPE = {
  FEED: i18nMark("feed"),
  FORUM: i18nMark("forum")
};

// CHANNEL GENERAL CONSTANTS

export const PAGE_SIZE = 10;

export const CHANNELS_FEATURE_FLAGS = {
  DWP_ALLOW_CHANNELS_MODULE: "dwp_allow_channels_module",
  COMMUNITY_ALLOW_STATS_MODULE: "community_allow_simple_stats"
};

export const CHANNEL_CONST = {
  TEAM: "team",
  CHANNEL: "channel",
  POST: "post",
  COMMENT: "comment",
  REPLY: "reply",
  REACTION: "reaction"
};

export const CHANNEL_EVENTS = {
  CHILD_ARCHIVE: "CHILD_ARCHIVE",
  CHILD_UNARCHIVE: "CHILD_UNARCHIVE",
  CHILD_UPDATE: "CHILD_UPDATE",
  CHILD_DELETE: "CHILD_DELETE",
  CHILD_SUBSCRIBE: "CHILD_SUBSCRIBE",
  CHILD_UNSUBSCRIBE: "CHILD_UNSUBSCRIBE",
  CHILD_POST_PINNED: "CHILD_POST_PINNED",
  CHILD_POST_UNPINNED: "CHILD_POST_UNPINNED"
};

export const FORUM_LAYOUTS = {
  VERTICAL_FORUM_VIEW: "verticalforumview"
};

export const ANNOUNCEMENTS_CHANNEL_ID = "announcements";

export const SYSTEM_CHANNEL_NAME = {
  ANNOUNCEMENTS: i18nMark("Announcements")
};

export const CHANNEL_ROLES = {
  ADMIN: "Admin",
  MODERATOR: "Moderator",
  MEMBER: "Member",
  VIEW: "View",
  ACCESSCHANNEL: "AccessChannel"
};

export const CHANNEL_STATUS = {
  LIVE: "Live",
  ARCHIVED: "Archived",
  PENDING: "Pending"
};

export const MAX_CHARACTERS = {
  DELETE_REASON: 50,
  NAME_SIZE: 140
};

export const ATTACHMENT_FILE_TYPES = {
  DOC: "DOC",
  DOCS: "DOCX",
  PDF: "PDF",
  PPT: "PPT",
  PPTX: "PPTX",
  XLS: "XLS",
  XLSX: "XLSX",
  TXT: "TXT",
  MP3: "MP3",
  JPG: "JPG",
  JPEG: "JPEG",
  JPE: "JPE",
  PNG: "PNG",
  GIF: "GIF"
};

export const REMOVE_POST_REASON = {
  INCORRECT: "Incorrect",
  INAPPROPRIATE: "Inappropriate",
  CONFIDENTIAL: "Confidential",
  OTHER: "Other"
};

export const REASONS_FOR_DELETION = {
  INCORRECT: {
    _id: "Incorrect",
    Name: i18nMark("Incorrect")
  },
  INAPPROPRIATE: {
    _id: "Inappropriate",
    Name: i18nMark("Inappropriate")
  },
  CONFIDENTIAL: {
    _id: "Confidential",
    Name: i18nMark("Confidential")
  },
  OTHER: {
    _id: "Other",
    Name: i18nMark("Other")
  }
};

export const ANNONCEMENT_THEME = {
  GENERAL: "General",
  CELEBRATION: "Celebration",
  APPRECIATION: "Appreciation",
  HEALTH: "Health"
};

export const CHANNEL_TYPES = {
  FEED: "Feed",
  FORUM: "Forum"
};

export const CHANNEL_NAMES = {
  Social: i18nMark("Channel name"),
  Forum: i18nMark("Forum name")
};

export const PIN_HELP_TEXT = i18nMark(
  "Reached the limit of {max_pin_limit} favorites. Remove one."
);

export const ICON_TYPES = {
  PRESET: "Preset",
  CUSTOM: "Custom"
};

export const GET_TOAST_MESSAGE = {
  Admin: i18nMark("{name} is now an admin for this channel"),
  Moderator: i18nMark("{name} is now a moderator for this channel"),
  Member: i18nMark("{name} is now a member for this channel")
};

export const FILE_PICKER_OPTIONS = {
  imageProps: {
    sizes: [
      [1200, 1200],
      [600, 600],
      [300, 300],
      [75, 75]
    ]
  },
  maxCount: 3,
  maxSize: 52428800,
  fileExtensions: ["*"]
};

export const TEXT_COLOR_LIST = [
  "var(--post-default)",
  "#09a7d9",
  "#6e6edb",
  "#7491f4",
  "#0565ff",
  "#4aa147",
  "#df4440",
  "#d341a5",
  "#eeb209",
  "#f58220"
];

export const BACKGROUND_TYPES = {
  COLOR: "Color",
  GRADIENT: "Gradient",
  IMAGE: "Image"
};

export const BACKGROUND_COLOR1 = "backgroundColor1";
export const BACKGROUND_COLOR2 = "backgroundColor2";
export const BACKGROUND_COLOR3 = "backgroundColor3";
export const BACKGROUND_COLOR4 = "backgroundColor4";
export const BACKGROUND_COLOR5 = "backgroundColor5";

export const BACKGROUND_COLOR_MAPPING = {
  [BACKGROUND_COLOR1]: "var(--background-default)",
  [BACKGROUND_COLOR2]: "#12c8d4",
  [BACKGROUND_COLOR3]: "#6b3bed",
  [BACKGROUND_COLOR4]: "#0085ff",
  [BACKGROUND_COLOR5]: "#ffa300"
};

export const BACKGROUND_COLOR_LIST = [
  BACKGROUND_COLOR1,
  BACKGROUND_COLOR2,
  BACKGROUND_COLOR3,
  BACKGROUND_COLOR4,
  BACKGROUND_COLOR5
];

export const BACKGROUND_GRADIENT1 = "backgroundGradient1";
export const BACKGROUND_GRADIENT2 = "backgroundGradient2";
export const BACKGROUND_GRADIENT3 = "backgroundGradient3";
export const BACKGROUND_GRADIENT4 = "backgroundGradient4";
export const BACKGROUND_GRADIENT5 = "backgroundGradient5";

export const BACKGROUND_GRADIENT_MAPPING = {
  [BACKGROUND_GRADIENT1]:
    "linear-gradient(225deg, rgb(142, 142, 255), rgb(101, 101, 224) 47%, rgb(98, 98, 222) 1%)",
  [BACKGROUND_GRADIENT2]:
    "linear-gradient(45deg, rgb(106, 88, 255) 0%, rgb(165, 12, 247) 63%, rgb(169, 6, 246))",
  [BACKGROUND_GRADIENT3]:
    "linear-gradient(225deg, rgb(235, 118, 209), rgb(214, 71, 170) 44%, rgb(234, 43, 127))",
  [BACKGROUND_GRADIENT4]:
    "linear-gradient(225deg, rgb(255, 117, 114), rgb(237, 75, 71) 43%, rgb(255, 65, 51))",
  [BACKGROUND_GRADIENT5]:
    "linear-gradient(225deg, rgb(255, 164, 86), rgb(252, 137, 39) 43%, rgb(253, 129, 24) 0%)"
};

export const BACKGROUND_GRADIENT_LIST = [
  BACKGROUND_GRADIENT1,
  BACKGROUND_GRADIENT2,
  BACKGROUND_GRADIENT3,
  BACKGROUND_GRADIENT4,
  BACKGROUND_GRADIENT5
];

export const BACKGROUND_IMAGE_LIST = [
  "backgroundImage1",
  "backgroundImage2",
  "backgroundImage3",
  "backgroundImage4",
  "backgroundImage5"
];

export const MAX_TEXT_LENGTH_FOR_BACKGROUND = 280;

export const COMMENT_ATTACHMENT_CONFIG = {
  MAX_COUNT: 3,
  MAX_SIZE: 10000000,
  IMAGE_PROPS: {
    sizes: [
      [1200, 1200],
      [600, 600],
      [300, 300],
      [75, 75]
    ]
  }
};

export const SPECIAL_MENTION_TYPES = {
  Feed: {
    Name: i18n._(t`Feed`),
    Type: "Channel",
    Description: i18n._(t`Notify all the members in this feed`)
  },
  Forum: {
    Name: i18n._(t`Forum`),
    Type: "Channel",
    Description: i18n._(t`Notify all the members in this forum`)
  },
  Tagged: {
    _id: "Tagged",
    Name: i18n._(t`Tagged`),
    Kind: "Tagged",
    Description: i18n._(t`Notify everyone tagged in this post`)
  }
};

// FEED SPECIFIC CONSTANTS

export const REACTIONS_LIST = [
  {
    reactionType: "Like",
    color: "var(--primary-blue-500)",
    color_pwa: "var(--blue-500)"
  },
  {
    reactionType: "FoldedHands",
    color: "var(--primary-blue-500)",
    color_pwa: "var(--blue-500)"
  },
  {
    reactionType: "Love",
    color: "var(--secondary-red-500)",
    color_pwa: "var(--red-500)"
  },
  {
    reactionType: "Haha",
    color: "var(--secondary-yellow-500)",
    color_pwa: "var(--orange-500)"
  },
  {
    reactionType: "Wow",
    color: "var(--secondary-yellow-500)",
    color_pwa: "var(--orange-500)"
  },
  {
    reactionType: "Sad",
    color: "var(--secondary-yellow-500)",
    color_pwa: "var(--orange-500)"
  },
  {
    reactionType: "Angry",
    color: "var(--secondary-red-500)",
    color_pwa: "var(--red-500)"
  },
  {
    reactionType: "Bouquet",
    color: "var(--secondary-green-500)",
    color_pwa: "var(--green-500)"
  },
  {
    reactionType: "Birthday",
    color: "var(--secondary-green-500)",
    color_pwa: "var(--green-500)"
  }
];

export const REACTION_TYPE = {
  Like: i18nMark("Like"),
  FoldedHands: i18nMark("Folded hands"),
  Love: i18nMark("Love"),
  Haha: i18nMark("Haha"),
  Wow: i18nMark("Wow"),
  Sad: i18nMark("Sad"),
  Angry: i18nMark("Angry"),
  Bouquet: i18nMark("Bouquet"),
  Birthday: i18nMark("Birthday")
};

export const REACTION_ICON = {
  Like,
  FoldedHands,
  Love,
  Sad,
  Wow,
  Haha,
  Bouquet,
  Angry,
  Birthday,
  LikeMain,
  LikeOutline,
  LikeOutlineBlack
};

// FORUM SPECIFIC CONTANTS

export const FORUM_CREATION_SETTING = {
  ADMIN: i18nMark("Admin"),
  EVERYONE: i18nMark("Everyone")
};

export const EDITOR_FORMATS = [
  "Paragraph",
  "Heading 1",
  "Heading 2",
  "Heading 3"
];

export const DATE_FORMAT_LONG = "DD MMM YYYY, hh:mma";

export const FILTERS = { MOST_UPVOTED: "most_upvoted", RECENT: "recent" };

export const FORUMS_TABS = [
  { key: FILTERS.RECENT, label: i18nMark("Recent") },
  { key: FILTERS.MOST_UPVOTED, label: i18nMark("Most upvoted") }
];

export const UPVOTE = "UpVote";

export const DOWNVOTE = "DownVote";

export const TOPIC_QUERY = JSON.stringify([
  { Id: "VoteCount", SortType: "DESC" }
]);

export const TOOLBARS = {
  BOLD: {
    type: FORMAT_TYPES.BOLD,
    button: BoldButton
  },
  ITALIC: {
    type: FORMAT_TYPES.ITALIC,
    button: ItalicButton
  },
  UNDERLINE: {
    type: FORMAT_TYPES.UNDERLINE,
    button: UnderlineButton
  },
  STRIKE: {
    type: FORMAT_TYPES.STRIKE,
    button: StrikeButton
  },
  COLOR: {
    type: FORMAT_TYPES.COLOR,
    button: TextColor,
    label: i18nMark("Color")
  },
  HEADER: {
    type: FORMAT_TYPES.HEADER,
    button: Heading
  },
  UL_LIST: {
    type: FORMAT_TYPES.UL_LIST,
    button: ULList,
    label: i18nMark("Bulleted list")
  },
  OL_LIST: {
    type: FORMAT_TYPES.OL_LIST,
    button: OLList,
    label: i18nMark("Numbered list")
  },
  CODE: {
    type: FORMAT_TYPES.CODE,
    button: CodeButton,
    label: i18nMark("Code")
  },
  BLOCKQUOTE: {
    type: FORMAT_TYPES.BLOCKQUOTE,
    button: BlockQuoteButton,
    label: i18nMark("Blockquote")
  },
  LINK: {
    type: FORMAT_TYPES.LINK,
    button: LinkButton,
    label: i18nMark("Link")
  }
};

export const HEADER = [TOOLBARS.HEADER];

export const INLINE_FORMATTERS = [
  TOOLBARS.BOLD,
  TOOLBARS.ITALIC,
  TOOLBARS.UNDERLINE,
  TOOLBARS.STRIKE,
  TOOLBARS.LINK,
  TOOLBARS.COLOR
];

export const BLOCK_FORMATTERS = [
  TOOLBARS.BLOCKQUOTE,
  TOOLBARS.CODE,
  TOOLBARS.UL_LIST,
  TOOLBARS.OL_LIST
];

// LIVESYNC EVENTS

export const EVENT_TYPES = {
  NEW_POST: "post_created",
  EDIT_POST: "post_updated",
  DELETE_POST: "post_deleted",
  NEW_COMMENT: "comment_created",
  EDIT_COMMENT: "comment_updated",
  DELETE_COMMENT: "comment_deleted",
  NEW_REPLY: "reply_created",
  EDIT_REPLY: "reply_updated",
  DELETE_REPLY: "reply_deleted",
  NEW_POST_REACTION: "post_reacted",
  DELETE_POST_REACTION: "post_unreacted",
  POST_PINNED: "post_pinned",
  POST_UNPINNED: "post_unpinned",
  NEW_COMMENT_REACTION: "comment_reacted",
  DELETE_COMMENT_REACTION: "comment_unreacted",
  NEW_REPLY_REACTION: "reply_reacted",
  DELETE_REPLY_REACTION: "reply_unreacted",
  TURN_OFF_COMMENTS: "comment_turned_off",
  TURN_ON_COMMENTS: "comment_turned_on",
  ADD_POLL_VOTE: "poll_voted",
  REMOVE_POLL_VOTE: "poll_unvoted",
  NEW_POLL_OPTION: "poll_option_added",
  POST_SEEN: "post_seen",
  TOPIC_UPVOTED: "topic_upvoted",
  TOPIC_DOWNVOTED: "topic_downvoted",
  TOPIC_UNREACTED: "post_unreacted",
  TOPIC_CLOSED: "post_closed",
  TOPIC_OPENED: "post_opened",
  TOPIC_SOLVED: "post_solved",
  TOPIC_UNSOLVED: "post_unsolved",
  COMMENT_UPVOTED: "comment_upvoted",
  COMMENT_DOWNVOTED: "comment_downvoted",
  COMMENT_UNREACTED: "comment_unreacted",
  BEST_SOLUTION_MARKED: "best_solution_marked",
  BEST_SOLUTION_UNMARKED: "best_solution_unmarked"
};

// ERROR CODES

export const ERROR_CODE = {
  KISSFLOW_ERROR_00010: "KISSFLOW_ERROR_00010", // You don't have permission to access this resource
  KISSFLOW_ERROR_06501: "KISSFLOW_ERROR_06501", // This comment can't be found
  KISSFLOW_ERROR_06502: "KISSFLOW_ERROR_06502", // This reply can't be found
  KISSFLOW_ERROR_04800: "KISSFLOW_ERROR_04800",
  // The live {flow_type} limit set for your current plan is {flow_limit}.
  // Archive the ones you don't need, or upgrade your plan.
  KISSFLOW_ERROR_06006: "KISSFLOW_ERROR_06006",
  KISSFLOW_ERROR_04100: "KISSFLOW_ERROR_04100", // {locked_by} has locked this {flow_type} - {model_name} for {event}.
  KISSFLOW_ERROR_04409: "KISSFLOW_ERROR_04409",
  KISSFLOW_ERROR_00015: "KISSFLOW_ERROR_00015", // This user does not exist in your account
  KISSFLOW_ERROR_00024: "KISSFLOW_ERROR_00024", // The trial period for this account has expired.
  KISSFLOW_ERROR_04305: "KISSFLOW_ERROR_04305",
  KISSFLOW_ERROR_04202: "KISSFLOW_ERROR_04202",
  KISSFLOW_ERROR_060102: "KISSFLOW_ERROR_060102",
  KISSFLOW_ERROR_23002: "KISSFLOW_ERROR_23002", // The post you're looking for cannot be found
  KISSFLOW_ERROR_060103: "KISSFLOW_ERROR_060103",
  KISSFLOW_ERROR_06998: "KISSFLOW_ERROR_06998", // This channel was archived
  KISSFLOW_ERROR_06997: "KISSFLOW_ERROR_06997" // This team was archived
};

// CHANNEL ICON TAB CONSTANTS

export const IMAGEBASEURL = `/upload/2`;

export const CHANNEL_ICON_URL = `${window.publicPath}media/images/channel/icons`;

export const ICON_COLOR1 = "Color1";
export const ICON_COLOR2 = "Color2";
export const ICON_COLOR3 = "Color3";
export const ICON_COLOR4 = "Color4";
export const ICON_COLOR5 = "Color5";
export const ICON_COLOR6 = "Color6";
export const ICON_COLOR7 = "Color7";
export const ICON_COLOR8 = "Color8";
export const ICON_COLOR9 = "Color9";
export const ICON_COLOR10 = "Color10";

export const CHANNEL_ICON_COLOR_LIST = [
  ICON_COLOR1,
  ICON_COLOR2,
  ICON_COLOR3,
  ICON_COLOR4,
  ICON_COLOR5,
  ICON_COLOR6,
  ICON_COLOR7,
  ICON_COLOR8,
  ICON_COLOR9,
  ICON_COLOR10
];

export const CHANNEL_ICON_IMAGEBASEURL =
  useAccountStore.getState().AppConfig?.ProfileBasePath;

export const CHANNEL_ICON_CONFIG = {
  imageProps: {
    sizes: [
      [1200, 1200],
      [600, 600],
      [300, 300],
      [75, 75]
    ],
    crop: true,
    aspectRatio: 1
  },
  maxSize: 5000000,
  minSize: 2,
  maxCount: 2,
  fileExtensions: ["JPG", "JPEG", "JPE", "PNG"],
  isCropMandatory: true,
  isProfile: true
};

export const GENERAL = "general";
export const ACCOUNTS = "accounts";
export const SALES = "sales";
export const PRODUCT = "product";
export const HR = "hr";
export const BUSINESS = "business";
export const SPORTS = "sports";

export const CHANNEL_ICON_TABS_LIST = [
  GENERAL,
  ACCOUNTS,
  SALES,
  PRODUCT,
  HR,
  BUSINESS,
  SPORTS
];

export const CHANNEL_ICON_TABS_MAPPING = {
  [GENERAL]: {
    title: i18nMark("General"),
    id: "general",
    icons: [
      `General1`,
      `General2`,
      `General3`,
      `General4`,
      `General5`,
      `General6`,
      `General7`,
      `General8`,
      `General9`,
      `General10`,
      `General11`,
      `General12`,
      `General13`,
      `General14`,
      `General15`,
      `General16`,
      `General17`,
      `General18`,
      `General19`,
      `General20`,
      `General21`,
      `General22`,
      `General23`,
      `General24`
    ]
  },
  [ACCOUNTS]: {
    title: i18nMark("Accounting & Finance"),
    id: "accounts",
    icons: [
      `Accounts1`,
      `Accounts2`,
      `Accounts3`,
      `Accounts4`,
      `Accounts5`,
      `Accounts6`,
      `Accounts7`,
      `Accounts8`,
      `Accounts9`,
      `Accounts10`,
      `Accounts11`,
      `Accounts12`,
      `Accounts13`,
      `Accounts14`,
      `Accounts15`,
      `Accounts16`,
      `Accounts17`,
      `Accounts18`,
      `Accounts19`,
      `Accounts20`,
      `Accounts21`,
      `Accounts22`,
      `Accounts23`,
      `Accounts24`
    ]
  },
  [SALES]: {
    title: i18nMark("Sales & Marketing"),
    id: "sales",
    icons: [
      `Sales1`,
      `Sales2`,
      `Sales3`,
      `Sales4`,
      `Sales5`,
      `Sales6`,
      `Sales7`,
      `Sales8`,
      `Sales9`,
      `Sales10`,
      `Sales11`,
      `Sales12`,
      `Sales13`,
      `Sales14`,
      `Sales15`,
      `Sales16`,
      `Sales17`,
      `Sales18`,
      `Sales19`,
      `Sales20`,
      `Sales21`,
      `Sales22`,
      `Sales23`,
      `Sales24`
    ]
  },
  [HR]: {
    title: i18nMark("HR & Operations"),
    id: "hr",
    icons: [
      `Hr1`,
      `Hr2`,
      `Hr3`,
      `Hr4`,
      `Hr5`,
      `Hr6`,
      `Hr7`,
      `Hr8`,
      `Hr9`,
      `Hr10`,
      `Hr11`,
      `Hr12`,
      `Hr13`,
      `Hr14`,
      `Hr15`,
      `Hr16`,
      `Hr17`,
      `Hr18`,
      `Hr19`,
      `Hr20`,
      `Hr21`,
      `Hr22`,
      `Hr23`,
      `Hr24`
    ]
  },
  [PRODUCT]: {
    title: i18nMark("Production & Manufacturing "),
    id: "product",
    icons: [
      `Product1`,
      `Product2`,
      `Product3`,
      `Product4`,
      `Product5`,
      `Product6`,
      `Product7`,
      `Product8`,
      `Product9`,
      `Product10`,
      `Product11`,
      `Product12`,
      `Product13`,
      `Product14`,
      `Product15`,
      `Product16`,
      `Product17`,
      `Product18`,
      `Product19`,
      `Product20`,
      `Product21`,
      `Product22`,
      `Product23`,
      `Product24`
    ]
  },
  [BUSINESS]: {
    title: i18nMark("Business"),
    id: "business",
    icons: [
      `Business1`,
      `Business2`,
      `Business3`,
      `Business4`,
      `Business5`,
      `Business6`,
      `Business7`,
      `Business8`,
      `Business9`,
      `Business10`,
      `Business11`,
      `Business12`,
      `Business13`,
      `Business14`,
      `Business15`,
      `Business16`,
      `Business17`,
      `Business18`,
      `Business19`,
      `Business20`,
      `Business21`,
      `Business22`,
      `Business23`,
      `Business24`
    ]
  },
  [SPORTS]: {
    title: i18nMark("Sports & Entertainment"),
    id: "sports",
    icons: [
      `Sports1`,
      `Sports2`,
      `Sports3`,
      `Sports4`,
      `Sports5`,
      `Sports6`,
      `Sports7`,
      `Sports8`,
      `Sports9`,
      `Sports10`,
      `Sports11`,
      `Sports12`,
      `Sports13`,
      `Sports14`,
      `Sports15`,
      `Sports16`,
      `Sports17`,
      `Sports18`,
      `Sports19`,
      `Sports20`,
      `Sports21`,
      `Sports22`,
      `Sports23`,
      `Sports24`
    ]
  }
};
